import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Eye, Check, Code, Building } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useBuilder } from "@/contexts/BuilderContext";
import { useParams } from "react-router-dom";
import { AvatarUpload } from "./AvatarUpload";
import { useToast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { OperatorSettings } from "./OperatorSettings";
import { PiiEmbedButton } from "./PiiEmbedButton";

interface BuilderHeaderProps {
  copied: boolean;
  onCopyEmbedCode: () => void;
}

export const BuilderHeader = ({
  copied,
  onCopyEmbedCode,
}: BuilderHeaderProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDescriptionEditing, setIsDescriptionEditing] = useState(false);
  const { id } = useParams();
  const { toast } = useToast();
  
  // Get data from unified context
  const { getChatbot, getSettings, updateBasicInfo } = useBuilder();
  const chatbot = getChatbot();
  const settings = getSettings();
  
  // Local state for editing
  const [chatName, setChatName] = useState(chatbot?.name || '');
  const [description, setDescription] = useState(chatbot?.description || '');

  useEffect(() => {
    setChatName(chatbot?.name || '');
  }, [chatbot?.name]);

  useEffect(() => {
    setDescription(chatbot?.description || '');
  }, [chatbot?.description]);

  const handleNameClick = () => {
    setIsEditing(true);
  };

  const handleDescriptionClick = () => {
    setIsDescriptionEditing(true);
  };

  const handleNameBlur = () => {
    handleNameSave();
  };

  const handleDescriptionBlur = () => {
    handleDescriptionSave();
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setChatName(chatbot?.name || '');
      setIsEditing(false);
    }
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleDescriptionSave();
    } else if (e.key === 'Escape') {
      setDescription(chatbot?.description || '');
      setIsDescriptionEditing(false);
    }
  };

  const handleNameSave = () => {
    if (chatName !== chatbot?.name) {
      updateBasicInfo({ name: chatName });
    }
    setIsEditing(false);
  };

  const handleDescriptionSave = () => {
    if (description !== chatbot?.description) {
      updateBasicInfo({ description });
    }
    setIsDescriptionEditing(false);
  };

  const handlePreview = () => {
    if (chatbot?.id) {
      window.open(`/chat/${chatbot.id}`, '_blank');
    }
  };

  return (
    <header className="mb-8 flex justify-between items-center">
      <div className="flex items-center gap-6">
        <AvatarUpload avatarUrl={chatbot?.avatar_url} name={chatName} />

        <div className="flex flex-col gap-2">
          {isEditing ? (
            <Input
              type="text"
              value={chatName}
              onChange={(e) => setChatName(e.target.value)}
              onKeyDown={handleNameKeyDown}
              onBlur={handleNameBlur}
              autoFocus
              className="text-3xl font-bold text-gray-900 w-[300px]"
            />
          ) : (
            <h1
              className="text-3xl font-bold text-gray-900 cursor-pointer hover:text-gray-700 transition-colors"
              onClick={handleNameClick}
            >
              {chatName}
            </h1>
          )}

          <div className="flex items-center gap-2">
            {isDescriptionEditing ? (
              <Input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={handleDescriptionKeyDown}
                onBlur={handleDescriptionBlur}
                autoFocus
                className="text-sm text-gray-600 w-[300px]"
                placeholder="Add a description"
              />
            ) : (
              <div
                onClick={handleDescriptionClick}
                className="text-sm text-gray-600 w-[300px] cursor-pointer hover:text-gray-800 transition-colors truncate"
              >
                {description || "Add a description"}
              </div>
            )}
          </div>

          {settings?.companyName && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Building className="h-4 w-4" />
              <span>{settings.companyName}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-4">
        <Button variant="outline" onClick={handlePreview} className="hover-scale">
          <Eye className="mr-2 h-4 w-4" />
          Preview
        </Button>
        <Button variant="outline" onClick={onCopyEmbedCode} className="hover-scale">
          {copied ? (
            <>
              <Check className="mr-2 h-4 w-4" />
              Copied!
            </>
          ) : (
            <>
              <Code className="mr-2 h-4 w-4" />
              Copy Embed Code
            </>
          )}
        </Button>
        <PiiEmbedButton chatbotId={id || ''} />
      </div>
    </header>
  );
};
